import { useEffect, useMemo, useRef } from 'react';

/**
 * Default options for IntersectionObserver
 */
const DEFAULT_OBSERVER_OPTIONS = {
  root: null,
  rootMargin: '0px',
  threshold: 1.0,
};

/**
 * Custom hook for infinite scroll functionality using Intersection Observer
 * @param {Object} options - Configuration options
 * @param {boolean} options.isLoading - Whether data is currently loading
 * @param {boolean} options.hasMore - Whether there are more items to load
 * @param {Function} options.onLoadMore - Callback function to load more items
 * @param {Object} options.observerOptions - Options for IntersectionObserver (root, rootMargin, threshold)
 * @returns {React.RefObject} - Ref to attach to the sentinel element
 */
const useInfiniteScroll = ({ isLoading, hasMore, onLoadMore, observerOptions }) => {
  const sentinelRef = useRef(null);
  const isFetchingNextPageRef = useRef(false);

  // Memoize options to normalize structure and use defaults for missing values
  // Caller should memoize observerOptions if provided to avoid unnecessary recreations
  const finalObserverOptions = useMemo(() => {
    if (!observerOptions) {
      return DEFAULT_OBSERVER_OPTIONS;
    }
    return {
      root: observerOptions.root ?? DEFAULT_OBSERVER_OPTIONS.root,
      rootMargin: observerOptions.rootMargin ?? DEFAULT_OBSERVER_OPTIONS.rootMargin,
      threshold: observerOptions.threshold ?? DEFAULT_OBSERVER_OPTIONS.threshold,
    };
  }, [observerOptions]);

  useEffect(() => {
    const handleIntersection = (entries) => {
      if (!entries?.[0]?.isIntersecting) return;
      if (isLoading) return;
      if (isFetchingNextPageRef.current) return;
      if (!hasMore) return;

      isFetchingNextPageRef.current = true;
      onLoadMore();
    };

    const observer = new IntersectionObserver(handleIntersection, finalObserverOptions);
    const node = sentinelRef.current;

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [isLoading, hasMore, onLoadMore, finalObserverOptions]);

  // Release the intersection guard when loading finishes
  useEffect(() => {
    if (!isLoading) {
      isFetchingNextPageRef.current = false;
    }
  }, [isLoading]);

  return sentinelRef;
};

export default useInfiniteScroll;
